/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent:     staff pick a calendar day in filters/forms — workbench density
 * Hierarchy:  value leads; clear link demoted
 * Palette:    control tokens (inputBackground + controlBorder)
 * Depth:      borders only · radius 12
 */
import React, { Fragment, useState } from 'react';
import { Modal } from '../modals/Modal';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { colors, spacing, typography } from '../../shared/theme/public-api';
import { posSharedUi as posUi } from './posSharedUi';
import { useModalBackdropPadding } from './hooks-ui/useModalBackdropPadding';
import { WebExpiryDateInput } from './WebExpiryDateInput';
import { createTodayDate, formatDateLabelEsAr, formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { iosDatePickerDisplay } from './iosDatePickerDisplay';
import { nativeTemporalTriggerStyle, type WebNativeTemporalSize } from './webNativeTemporalInput';

export type InventoryIsoDateFieldProps = {
  value: string;
  onChange: (iso: string) => void;
  emptyLabel: string;
  ariaLabel?: string;
  showClearButton?: boolean;
  clearButtonLabel?: string;
  iosEmptyBehavior?: 'seedToday' | 'openPicker';
  /** Web: alto del input nativo (default 'comfortable' 48, 'compact' 40). */
  size?: WebNativeTemporalSize;
  /**
   * Web: min="hoy" en el <input type="date"> (guía visual del picker, no bloquea
   * pegar una fecha pasada). Nativo no restringe: un lote vencido se puede
   * cargar igual — la advertencia la muestra el formulario.
   */
  minToday?: boolean;
};

export const InventoryIsoDateField = ({
  value,
  onChange,
  emptyLabel,
  ariaLabel = 'Fecha',
  showClearButton = true,
  clearButtonLabel = 'Quitar vencimiento',
  iosEmptyBehavior = 'seedToday',
  size = 'comfortable',
  minToday = false,
}: InventoryIsoDateFieldProps) => {
  const iosModalBackdropPadding = useModalBackdropPadding();
  const iosMode = iosDatePickerDisplay();
  const [androidPickerVisible, setAndroidPickerVisible] = useState(false);
  const [androidDraft, setAndroidDraft] = useState<Date>(createTodayDate);
  const [iosFilterModalOpen, setIosFilterModalOpen] = useState(false);
  const [iosFilterDraft, setIosFilterDraft] = useState<Date>(createTodayDate);

  const clear = () => {
    onChange('');
    setAndroidPickerVisible(false);
    setIosFilterModalOpen(false);
  };
  const seedToday = () => onChange(formatIsoDate(createTodayDate()));
  const openAndroidPicker = () => {
    setAndroidDraft(value.trim() ? parseIsoDate(value) : createTodayDate());
    setAndroidPickerVisible(true);
  };
  const onAndroidValueChange = (_event: unknown, date: Date) => {
    setAndroidPickerVisible(false);
    onChange(formatIsoDate(date));
  };
  const onAndroidDismiss = () => {
    setAndroidPickerVisible(false);
  };
  const openIosFilterPicker = () => {
    setIosFilterDraft(value.trim() ? parseIsoDate(value) : createTodayDate());
    setIosFilterModalOpen(true);
  };
  const confirmIosFilter = () => {
    onChange(formatIsoDate(iosFilterDraft));
    setIosFilterModalOpen(false);
  };

  if (Platform.OS === 'web') {
    return (
      <View style={styles.wrap}>
        <WebExpiryDateInput
          value={value}
          onChange={onChange}
          ariaLabel={ariaLabel}
          size={size}
          min={minToday ? formatIsoDate(createTodayDate()) : undefined}
        />
        {showClearButton && value ? (
          <TouchableOpacity style={styles.expiryClearButton} onPress={clear}>
            <Text style={posUi.linkText}>{clearButtonLabel}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  if (Platform.OS === 'android') {
    return (
      <Fragment>
        <View style={styles.wrap}>
          <TouchableOpacity
            style={[styles.expiryPickerTrigger, value ? styles.expiryPickerTriggerFilled : null]}
            onPress={openAndroidPicker}
            accessibilityRole="button"
            accessibilityLabel={ariaLabel}
          >
            <View style={styles.expiryPickerTextWrap}>
              <Text style={styles.expiryPickerValue}>{value ? formatDateLabelEsAr(value) : emptyLabel}</Text>
            </View>
            <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
          {showClearButton && value ? (
            <TouchableOpacity style={styles.expiryClearButton} onPress={clear}>
              <Text style={posUi.linkText}>{clearButtonLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {androidPickerVisible ? (
          <DateTimePicker
            value={androidDraft}
            mode="date"
            display="default"
            onValueChange={onAndroidValueChange}
            onDismiss={onAndroidDismiss}
            locale="es-AR"
          />
        ) : null}
      </Fragment>
    );
  }

  return (
    <View style={styles.wrap}>
      {!value ? (
        <TouchableOpacity
          style={styles.expiryPickerTrigger}
          onPress={iosEmptyBehavior === 'openPicker' ? openIosFilterPicker : seedToday}
          accessibilityRole="button"
          accessibilityLabel={ariaLabel}
        >
          <View style={styles.expiryPickerTextWrap}>
            <Text style={styles.expiryPickerValue}>{emptyLabel}</Text>
          </View>
          <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
        </TouchableOpacity>
      ) : (
        <>
          <DateTimePicker
            value={parseIsoDate(value)}
            mode="date"
            display={iosMode}
            onValueChange={(_event, date) => onChange(formatIsoDate(date))}
            locale="es-AR"
            style={iosMode === 'inline' ? styles.iosInlinePicker : styles.iosSpinnerPicker}
          />
          {showClearButton ? (
            <TouchableOpacity style={styles.expiryClearButton} onPress={clear}>
              <Text style={posUi.linkText}>{clearButtonLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </>
      )}
      <Modal visible={iosFilterModalOpen} transparent animationType="fade" onRequestClose={() => setIosFilterModalOpen(false)}>
        <View style={[styles.iosFilterBackdrop, iosModalBackdropPadding]}>
          <View style={styles.iosFilterCard}>
            <Text style={styles.iosFilterTitle}>Elegir fecha</Text>
            <DateTimePicker
              value={iosFilterDraft}
              mode="date"
              display="spinner"
              onValueChange={(_e, date) => setIosFilterDraft(date)}
              locale="es-AR"
              style={styles.iosSpinnerPicker}
            />
            <View style={styles.iosFilterActions}>
              <TouchableOpacity style={posUi.secondaryButton} onPress={() => setIosFilterModalOpen(false)}>
                <Text style={posUi.secondaryButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={posUi.primaryButton} onPress={confirmIosFilter}>
                <Text style={posUi.primaryButtonText}>Listo</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { gap: spacing.xs },
  expiryPickerTrigger: {
    ...nativeTemporalTriggerStyle,
  },
  expiryPickerTriggerFilled: {
    borderColor: colors.accent,
    backgroundColor: colors.surface,
  },
  expiryPickerTextWrap: { flex: 1, minWidth: 0, gap: 2 },
  expiryPickerValue: { ...typography.body, color: colors.textPrimary, fontWeight: '600' },
  expiryClearButton: { alignSelf: 'flex-start' },
  iosInlinePicker: { alignSelf: 'stretch', width: '100%', minHeight: 360 },
  iosSpinnerPicker: { alignSelf: 'center', width: '100%', height: 216 },
  iosFilterBackdrop: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'center' },
  iosFilterCard: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    padding: spacing.md,
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: colors.controlBorder,
  },
  iosFilterTitle: { ...typography.body, fontWeight: '700', color: colors.textPrimary },
  iosFilterActions: { flexDirection: 'row', gap: spacing.sm, justifyContent: 'flex-end' },
});
